import { CardLayout } from '@blinq-types';

export type ImageType = 'logo' | 'profile' | 'cover';
export type ImageFieldId = 'profile_picture_img' | 'cover_photo_img' | 'company_logo_img';
/** Returns the field ID for the image type. Used to identify locked fields. */
export const typeToFieldId = (type: ImageType): ImageFieldId => {
  switch (type) {
    case 'profile':
      return 'profile_picture_img';
    case 'cover':
      return 'cover_photo_img';
    case 'logo':
      return 'company_logo_img';
  }
};

export const isImageLinked = (
  type: ImageType | undefined,
  isPreset: boolean,
  linkedAttributes?: string[],
): boolean => {
  if (!isPreset || !type) {
    return false;
  }

  return linkedAttributes?.includes(typeToFieldId(type)) || false;
};

export type LocationKey = 'banner' | 'left' | 'right';

type Locations = { [K in LocationKey]?: ImageType };

export const imageLocationsFromLayout = (
  layout: CardLayout,
  previews?: { left: boolean; right: boolean } | undefined,
): Locations => {
  const result: Locations = {};
  switch (layout) {
    case '1A':
      result.banner = 'profile';
      break;
    case '1B':
      result.banner = 'logo';
      break;
    case '1C':
      result.banner = 'cover';
      break;
    case '2A':
      result.banner = 'profile';
      result.left = 'logo';
      break;
    case '2B':
      result.banner = 'logo';
      result.left = 'profile';
      break;
    case '2C':
      result.banner = 'cover';
      result.left = 'profile';
      break;
    case '2D':
      result.banner = 'cover';
      result.left = 'logo';
      break;
    case '3A':
      result.banner = 'cover';
      result.right = 'logo';
      result.left = 'profile';
  }
  if (previews?.left && result.banner !== 'profile') {
    result.left ??= 'profile';
  }
  if (previews?.right && result.banner !== 'logo') {
    result.right ??= 'logo';
  }
  // default case
  return result;
};

export const imagesInLayout = (layout: CardLayout): ImageType[] => {
  const locations = imageLocationsFromLayout(layout);
  return [...new Set(Object.values(locations))];
};

export function allLayouts(): CardLayout[] {
  return [
    CardLayout.Layout_1A,
    CardLayout.Layout_1B,
    CardLayout.Layout_1C,
    CardLayout.Layout_2A,
    CardLayout.Layout_2B,
    CardLayout.Layout_2C,
    CardLayout.Layout_2D,
    CardLayout.Layout_3A,
  ];
}

export const hasLayouts = ({
  cover,
  logo,
  profile,
}: {
  cover?: boolean | string;
  logo?: boolean | string;
  profile?: boolean | string;
}): CardLayout[] => {
  const layouts: CardLayout[] = [];
  if (profile) layouts.push(CardLayout.Layout_1A);
  if (logo) layouts.push(CardLayout.Layout_1B);
  if (cover) layouts.push(CardLayout.Layout_1C);

  if (profile && logo) {
    layouts.push(CardLayout.Layout_2A);
    layouts.push(CardLayout.Layout_2B);
  }
  if (profile && cover) layouts.push(CardLayout.Layout_2C);
  if (cover && logo) layouts.push(CardLayout.Layout_2D);
  if (profile && logo && cover) layouts.push(CardLayout.Layout_3A);

  return layouts;
};

export const canUseLayout = (
  p: {
    cover?: boolean | string;
    logo?: boolean | string;
    profile?: boolean | string;
  },
  layout: CardLayout,
): boolean => {
  const layouts = hasLayouts(p);
  return !!layouts.find((_) => _ === layout);
};

export const getSuggestedLayout = (p: {
  cover?: boolean | string;
  logo?: boolean | string;
  profile?: boolean | string;
}): CardLayout | undefined => {
  if (p.cover && p.logo && p.profile) {
    return CardLayout.Layout_3A;
  } else if (p.cover && p.logo) {
    return CardLayout.Layout_2D;
  } else if (p.cover && p.profile) {
    return CardLayout.Layout_2C;
  } else if (p.logo && p.profile) {
    return CardLayout.Layout_2B;
  } else if (p.cover) {
    return CardLayout.Layout_1C;
  } else if (p.profile) {
    return CardLayout.Layout_1A;
  } else if (p.logo) {
    return CardLayout.Layout_1B;
  } else {
    return undefined;
  }
};

export const getImagesRequiresForLayout = (
  p: {
    cover?: boolean | string;
    logo?: boolean | string;
    profile?: boolean | string;
  },
  layout: CardLayout,
): ImageType[] => {
  const requiredImages: ImageType[] = [];
  if (canUseLayout(p, layout)) {
    return requiredImages;
  } else {
    switch (layout) {
      case '1A': {
        requiredImages.push('profile');
        break;
      }
      case '1B': {
        requiredImages.push('logo');
        break;
      }
      case '1C': {
        requiredImages.push('cover');
        break;
      }
      case '2B':
      case '2A': {
        if (!p.profile) requiredImages.push('profile');
        if (!p.logo) requiredImages.push('logo');
        break;
      }
      case '2C': {
        if (!p.profile) requiredImages.push('profile');
        if (!p.cover) requiredImages.push('cover');
        break;
      }
      case '2D': {
        if (!p.logo) requiredImages.push('logo');
        if (!p.cover) requiredImages.push('cover');
        break;
      }
      case '3A': {
        if (!p.profile) requiredImages.push('profile');
        if (!p.logo) requiredImages.push('logo');
        if (!p.cover) requiredImages.push('cover');
        break;
      }
    }
    return [...new Set(requiredImages)];
  }
};
